import { Search, User, Users } from 'lucide-react';
import { useBoardView } from './useBoardView';

/**
 * The bar above the board. Search, and for an admin the view switch.
 *
 * A button that LOOKS like an input, not an input. Typing happens in the panel, so a
 * real field here would mean two boxes to keep in step and a first keystroke to carry
 * across. Nothing is typed into this one, so it does not pretend to accept anything.
 *
 * Filtering lives on each column header rather than here: a board-wide control would
 * ask one question of five columns and refetch four of them to answer a question about
 * the fifth. The view switch is the exception that proves it — it genuinely changes
 * what all five columns are about.
 */
export function BoardToolbar({ onOpen }: { onOpen: () => void }) {
  const { canSwitch, fullView, toggle } = useBoardView();

  return (
    <div
      className="flex shrink-0 items-center gap-2 border-b px-4 py-2"
      style={{ borderColor: 'var(--border)', background: 'var(--surface)' }}
    >
      {/* Balances the switch on the right so search stays centred. Desktop only —
          on a phone the search button takes the width the switch does not. */}
      {canSwitch && <div className="hidden flex-1 sm:block" aria-hidden />}

      <button
        type="button"
        onClick={onOpen}
        aria-label="Search tasks"
        aria-haspopup="dialog"
        className="mx-auto flex min-h-9 w-full items-center gap-2 rounded-lg border px-3 text-sm transition-colors sm:w-72"
        style={{
          borderColor: 'var(--border)',
          background: 'var(--surface-sunken)',
          color: 'var(--text-faint)',
        }}
      >
        <Search size={14} className="shrink-0" />
        <span className="truncate">Search a title, or S-142</span>
      </button>

      {canSwitch && (
        <div className="flex shrink-0 justify-end sm:flex-1">
          {/*
            role="switch" rather than two buttons: there is one setting with two
            states, and a screen reader should say "all tasks, on" instead of reading
            a label whose meaning flips every time it is pressed.

            The label says what you are SEEING, not what pressing it will do. A control
            that describes its own future is the standard way to make someone press it
            twice to work out which state they are in.
          */}
          <button
            type="button"
            role="switch"
            aria-checked={fullView}
            aria-label="Show everyone's tasks"
            title={
              fullView
                ? 'Showing every task in the company — press to see only yours'
                : 'Showing only tasks assigned to you — press to see everyone'
            }
            onClick={toggle}
            className="flex min-h-9 items-center gap-1.5 rounded-lg border px-2.5 text-xs font-medium transition-colors"
            style={{
              borderColor: fullView ? 'var(--accent)' : 'var(--border)',
              background: fullView ? 'var(--accent-soft)' : 'var(--surface-sunken)',
              color: fullView ? 'var(--accent)' : 'var(--text-muted)',
            }}
          >
            {fullView ? <Users size={14} className="shrink-0" /> : <User size={14} className="shrink-0" />}
            <span className="hidden sm:inline">{fullView ? 'All tasks' : 'My tasks'}</span>
          </button>
        </div>
      )}
    </div>
  );
}
